/**
 * usePhilipsSpeechMikeCommands
 * ----------------------------
 * Owns every global keyboard shortcut for the patient report screen.
 *
 * The Philips SpeechMike (in HID/keyboard mode) maps its physical buttons to
 * F-key presses, so this hook is the single place where every F-key binding
 * for the report screen lives. The same shortcuts also work from a regular
 * keyboard.
 *
 * Event Mode + Philips Device Connector is handled by `usePhilipsDeviceConnector`
 * with identical actions via `philipsSpeechMikeActions`.
 *
 * Keymap:
 *   F2          → toggle the unified sidebar mic (routes to report or findings by last focus)
 *   F3          → toggle the EDITOR (report engine) mic in DICTATION mode
 *                 (command mode is always forced OFF)
 *   Shift+F3    → toggle COMMAND MODE for the editor mic (auto-starts the
 *                 editor mic if it isn't already running). We use Shift —
 *                 not Ctrl or Cmd — because macOS reserves Ctrl+F3 for
 *                 "Move focus to the Dock" and intercepts the keydown
 *                 before the browser can see it.
 *   F4          → next paragraph (Shift+F4 → previous paragraph)
 *   F6          → next paragraph
 *   F7          → jump cursor to the IMPRESSION section
 *   F8          → jump cursor to the FINDINGS section
 *   F9          → save report (Radical / non-Wix only)
 *   F10         → run "Process AI"
 *
 * @param {import("./philipsSpeechMikeActions").PhilipsSpeechMikeCommandOptions} options
 */
import { useEffect, useRef } from "react";

import {
  PHILIPS_CMD,
  dispatchPhilipsSpeechMikeCommand,
} from "./philipsSpeechMikeActions";

export default function usePhilipsSpeechMikeCommands(options) {
  const propsRef = useRef(options);
  const micToggleInFlightRef = useRef(false);

  useEffect(() => {
    propsRef.current = options;
  });

  useEffect(() => {
    const runMicToggle = async (action) => {
      if (micToggleInFlightRef.current) return;
      micToggleInFlightRef.current = true;
      try {
        await action();
      } finally {
        micToggleInFlightRef.current = false;
      }
    };

    const onKeyDown = async (e) => {
      const p = propsRef.current;
      if (!p) return;
      if (e.repeat) return;

      const code = e.code;
      const prevent = () => {
        e.preventDefault();
        e.stopPropagation();
      };

      let command = null;

      if (code === "F2") {
        command = PHILIPS_CMD.SIDEBAR_MIC;
      } else if (code === "F3" && e.shiftKey) {
        command = PHILIPS_CMD.COMMAND_MODE;
      } else if (code === "F3" && !e.ctrlKey && !e.metaKey && !e.altKey) {
        command = PHILIPS_CMD.EDITOR_DICTATION;
      } else if (code === "F4") {
        command = e.shiftKey
          ? PHILIPS_CMD.PREV_PARAGRAPH
          : PHILIPS_CMD.NEXT_PARAGRAPH;
      } else if (code === "F6") {
        command = PHILIPS_CMD.NEXT_PARAGRAPH;
      } else if (code === "F7") {
        command = PHILIPS_CMD.IMPRESSION;
      } else if (code === "F8") {
        command = PHILIPS_CMD.FINDINGS;
      } else if (code === "F9") {
        command = PHILIPS_CMD.SAVE;
      } else if (code === "F10") {
        command = PHILIPS_CMD.PROCESS_AI;
      }

      if (!command) return;

      if (command !== PHILIPS_CMD.SAVE || (!p.isWixAuth && !p.isCreAuth)) {
        prevent();
      }

      if (command === PHILIPS_CMD.SAVE && (p.isWixAuth || p.isCreAuth)) {
        return;
      }

      await dispatchPhilipsSpeechMikeCommand(command, p, runMicToggle);
    };

    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, []);
}
